import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { CompetitionRegistrationService } from '../services/competition-registration.service';
import { CompetitionRegistration } from '../models/competition-registration.interface';
import { Subscription } from 'rxjs';
import { SelectedItem } from 'src/app/controls/models/selected-item.interface';
import { CompetitorPipe } from '../pipes/competitor.pipe';
import { ItemListSelectorComponent } from 'src/app/controls/item-list-selector/item-list-selector.component';
import { CompetitionRegistrationPrefill } from '../models/competition-registration-prefill.interface';
import { SnackBarService } from 'src/app/controls/snack-bar/services/snack-bar.service';
import { SelectComponent } from 'src/app/controls/select/select.component';
import { DateRange } from 'src/app/controls/date-range-picker/models/date-range.interface';
import { DateRangePickerComponent } from 'src/app/controls/date-range-picker/components/date-range-picker.component';
import { CompetitionRegistrationForm } from '../models/competition-registration-form.interface';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'cec-competition-registration-dialog',
  templateUrl: './competition-registration.component.html',
  styleUrls: ['./competition-registration.component.scss'],
  standalone: true,
  imports: [
    DialogComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatLabel,
    MatSelectModule,
    MatInputModule,
    MatListModule,
    MatDatepickerModule,
    CommonModule,
    CompetitorPipe,
    ItemListSelectorComponent,
    SelectComponent,
    DateRangePickerComponent
  ],
})
export class CompetitionRegistrationComponent implements OnInit, OnDestroy {
  /** Dialog template reference */
  @ViewChild('dialog') dialog!: DialogComponent;

  protected formGroup: FormGroup<CompetitionRegistrationForm> | undefined = undefined;

  private builder: FormBuilder = inject(FormBuilder);

  private service = inject(CompetitionRegistrationService);

  private snackBar: SnackBarService = inject(SnackBarService);

  private translate: TranslateService = inject(TranslateService);

  private router = inject(Router);

  /** Activated Route reference */
  private route = inject(ActivatedRoute);

  protected updated = new Date();

  private sub = new Subscription();

  protected crews: SelectedItem[] | undefined = undefined;

  protected competitors: SelectedItem[] | undefined = undefined;

  protected competitionName: string | undefined = undefined;

  private competitionId: number | undefined = undefined;

  protected id: number | undefined = undefined;

  private cdr = inject(ChangeDetectorRef);

  @Input() type: 'basic' | 'organizer' = 'basic';

  private navigate = false;

  constructor() {
    this.formGroup = this.builder.group({
      crewId: [null as number | null, Validators.required],
      driverId: [null as number | null, Validators.required],
      navigatorId: [null as number | null, Validators.required],
      accommodationDuration: [null as DateRange | null, Validators.required],
      noteCompetitor: [null as string | null],
      additionalCompetitors: []
    });

    this.sub = this.formGroup.get('crewId').valueChanges.subscribe((value: number) => {
      if (!value) {
        this.disableFields();
        return;
      }

      if (!this.id) {
        this.service.getCompetitors(value).subscribe((crew) => {
          this.competitors = crew?.competitors;
          this.formGroup.patchValue({
            driverId: crew.driverId,
            navigatorId: crew.navigatorId
          })
        });
      }
      
      this.enableFields();
    });

    this.sub.add(this.formGroup.get('driverId').valueChanges.subscribe(() => {
      this.updated = new Date();
    }));

    this.sub.add(this.formGroup.get('navigatorId').valueChanges.subscribe(() => {
      this.updated = new Date();
    }));

    this.sub.add(this.formGroup.get('additionalCompetitors').valueChanges.subscribe(() => {
      this.updated = new Date();
    }));
  }

  ngOnInit(): void {
    /** detects changes because of form validity issues */
    this.cdr.detectChanges();
  }
  
  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /**
   * Shows the competition registration dialog
   */
  public show(competitionId: number, isOrganizer = false, id?: number, navigate = false): void {

    this.id = id;
    this.competitionId = competitionId;
    this.navigate = navigate;

    if (!id && isOrganizer && this.type === 'organizer') {
      this.disableFields();
      this.prefillOrganizerData(competitionId);
      this.dialog.show();
      return;
    }
    
    /** fills data, if existing competition registration is edited */
    if (id) {
      this.fillData(id);
    } else {
      /** pre-fills data for new competition registration */
      this.prefillBasicData(competitionId);
    }

    this.dialog.show();
  }

  /**
   * Fills the form with existing data 
   * @param id the id of existing competition registration
   */
  private fillData(id: number): void {
    this.service
      .getCompetitionRegistration(id)
      .subscribe((data: CompetitionRegistration) => {
        this.crews = data.crews;
        this.competitors = data.competitors;
        this.formGroup.patchValue({ ...data });
        this.formGroup.get('crewId').disable();
      });
  }

  /**
   * Fills the form with existing data 
   * @param id the id of existing competition registration
   */
  private prefillBasicData(id: number): void {
    this.service
      .getDataForPrefill(id)
      .subscribe((data: CompetitionRegistrationPrefill) => {
        this.competitionName = data.competitionName;
        this.formGroup.patchValue({ 
          crewId: data.crewId,
          accommodationDuration: data.competitionDuration
         });

        this.crews = data.crews;
        if (!this.crews || !this.crews.length) {
          this.formGroup.get('crewId').disable({ emitEvent: false });
          this.disableFields();
        }
      });
  }

  /**
   * Fills the form with existing data 
   * @param id the id of existing competition registration
   */
  private prefillOrganizerData(id: number): void {
    this.service
      .getOrganizerDataForPrefill(id)
      .subscribe((data: CompetitionRegistrationPrefill) => {
        this.competitionName = data.competitionName;
        this.formGroup.patchValue({ 
          accommodationDuration: data.competitionDuration
         });

        this.crews = data.crews;
        if (!this.crews || !this.crews.length) {
          this.formGroup.get('crewId').disable({ emitEvent: false });
          this.disableFields();
        }
      });
  }

  /**
   * Disables driver and navigator fields
   */
  private enableFields(): void {
    this.formGroup.get('driverId').enable();
    this.formGroup.get('navigatorId').enable();
    this.formGroup.get('additionalCompetitors').enable();
  }

  /**
   * Disables driver and navigator fields
   */
  private disableFields(): void {
    this.formGroup.get('driverId').disable();
    this.formGroup.get('navigatorId').disable();
    this.formGroup.get('additionalCompetitors').disable();
  }

  /**
   * Executes the competition registration logic according to the button clicked
   * @param success the type of button clicked
   */
  protected onClickButton(success: boolean): void {
    /** if cancel button is clicked, just close the dialog */
    if (!success) {
      this.formGroup.get('crewId').enable({ emitEvent: false });
      return;
    }

    if (!this.id) {
      /** if id is not provided, creates new competition registration */
      this.service.create({
        ...this.formGroup.value,
        competitionId: this.competitionId,
        id: undefined
      }).subscribe((result) => {
        if (result) {
          /** shows success snackbar when crew is registered to competition */
          this.snackBar.success(this.translate.instant('CrewRegisteredToCompetition'));

          /** reloads registered crews table */
          this.service.reload.next();

          /** navigates to the competition registration list if necessary */
          if (this.navigate) {
            this.router.navigate(['/competition/' + this.competitionId + '/registered-crews'], { relativeTo: this.route });
          }

          return;
        }

        /** shows danger snackbar when crew registration to competition fails */
        this.snackBar.danger(this.translate.instant('UnableToRegisterCrewToCompetition'));
      });
    } else {
      /** if id is provided, update existing competition registration */
      this.service.update({
        id: this.id,
        ...this.formGroup.value,
        competitionId: this.competitionId
      }).subscribe((result) => {
        if (result) {
          /** shows success snackbar when competition registration is updated */
          this.snackBar.success(this.translate.instant('CrewRegistrationUpdateSuccess'));

          /** reloads registered crews table */
          this.service.reload.next();

          /** navigates to the competition registration list if necessary */
          if (this.navigate) {
            this.router.navigate(['/competition/' + this.competitionId + '/registered-crews'], { relativeTo: this.route });
          }

          return;
        }

        /** shows danger snackbar when update of competition registration fails */
        this.snackBar.danger(this.translate.instant('CrewRegistrationUpdateFailed'));
      });
    }
  }
}

